import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Info } from "lucide-react";
import ComplianceItem from "@/components/ComplianceItem";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import EmergencyContact from "@/components/EmergencyContactForm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const slug = (s) => String(s).replace(/\W+/g, "-").toLowerCase();
const triggerCls = "rounded-sm bg-[#141414] border-[#262626] h-11";

export function Field({ label, children, testid, hint }) {
  return (
    <div className="mb-7" data-testid={testid}>
      <Label className="font-mono-label text-[11px] text-muted-foreground mb-2 block leading-relaxed">{label}</Label>
      {children}
      {hint && (
        <p className="mt-2 text-xs text-muted-foreground flex gap-2 leading-relaxed">
          <Info size={14} className="shrink-0 mt-0.5 text-[#FFD600]" /> <span>{hint}</span>
        </p>
      )}
    </div>
  );
}

export function Sel({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={triggerCls}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          const l = typeof o === "string" ? o : o.label;
          return <SelectItem key={v} value={v} data-testid={`${testid}-option-${slug(v)}`}>{l}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  );
}

function CheckGrid({ options, selected, onToggle, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 brutal-border" data-testid={testid}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        const checked = selected.includes(v);
        return (
          <label
            key={v}
            data-testid={`${testid}-${slug(v)}`}
            className={`flex items-center gap-3 p-4 min-h-[52px] border-r border-b border-[#262626] cursor-pointer hover:bg-[#141414] transition-colors ${checked ? "bg-[#141414]" : ""}`}
          >
            <Checkbox checked={checked} onCheckedChange={() => onToggle(v)} className="h-5 w-5" />
            <span className="text-sm">{l}</span>
          </label>
        );
      })}
    </div>
  );
}

const toggleIn = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

function YesBox({ checked, onChange, testid, label }) {
  return (
    <label className="flex items-center gap-3 p-4 min-h-[52px] brutal-border cursor-pointer hover:bg-[#141414] transition-colors" data-testid={testid}>
      <Checkbox checked={!!checked} onCheckedChange={(c) => onChange(!!c)} className="h-5 w-5" />
      <span className="text-sm">{label}</span>
    </label>
  );
}

export function StepOperation({ profile, update, meta, t }) {
  return (
    <div>
      <Field label={t("operationState")} testid="field-op-state">
        <Sel value={profile.operation_state} onChange={(v) => update({ operation_state: v })} options={meta.us_states} placeholder="Select operation state" testid="select-operation-state" />
      </Field>
      <Field label={t("operationType")} testid="field-op-type">
        <Sel value={profile.operation_type} onChange={(v) => update({ operation_type: v })} options={meta.operation_types} placeholder="Interstate or Intrastate" testid="select-operation-type" />
      </Field>
      <Field label="What type of property will you transport?" testid="field-property-type">
        <Sel value={profile.property_type} onChange={(v) => update({ property_type: v })} options={meta.property_types} placeholder="Select property type" testid="select-property-type" />
      </Field>
      <Field label="How will your company operate?" testid="field-carrier-type">
        <CheckGrid
          testid="carrier-type"
          options={[
            { value: "For-Hire", label: "For-Hire Carrier (transporting goods or passengers for payment)" },
            { value: "Private", label: "Private Carrier (transporting your own company's goods)" },
          ]}
          selected={profile.carrier_types || []}
          onToggle={(v) => update({ carrier_types: toggleIn(profile.carrier_types || [], v) })}
        />
      </Field>
      <Field label="Will your company operate in multiple states or jurisdictions requiring IFTA?" testid="field-ifta"
        hint="IFTA applies to motor carriers operating vehicles over 26,000 lbs or with 3 or more axles in two or more states.">
        <YesBox checked={profile.ifta_required} onChange={(v) => update({ ifta_required: v })} testid="checkbox-ifta" label="Yes" />
      </Field>
      <Field label="Will your company operate in multiple states or jurisdictions requiring IRP?" testid="field-irp"
        hint="IRP applies to vehicles over 26,000 lbs traveling across state lines.">
        <YesBox checked={profile.irp_required} onChange={(v) => update({ irp_required: v })} testid="checkbox-irp" label="Yes" />
      </Field>
      <Field label="Will your drivers be required to keep a record of duty status (RODS) using an ELD?" testid="field-eld-rods"
        hint="ELD mandates apply to most commercial drivers required to keep RODS under FMCSA regulations.">
        <YesBox checked={profile.eld_rods} onChange={(v) => update({ eld_rods: v })} testid="checkbox-eld-rods" label="Yes" />
      </Field>
      <Field label="Will your driver operate under the ELD short-haul exception?" testid="field-short-haul"
        hint="The short-haul exception applies to drivers who operate within a 150 air-mile radius of their work reporting location and return to the same location each day.">
        <YesBox checked={profile.eld_short_haul} onChange={(v) => update({ eld_short_haul: v })} testid="checkbox-short-haul" label="Yes" />
      </Field>
    </div>
  );
}

function VehicleDetail({ v, detail, meta, onChange }) {
  const id = slug(v);
  return (
    <div className="p-4 border-b border-[#262626] last:border-b-0" data-testid={`vehicle-detail-${id}`}>
      <div className="font-heading font-bold text-sm mb-3">{v}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Number of axles</Label>
          <Sel value={detail.axles} onChange={(a) => onChange({ ...detail, axles: a })} options={meta.axle_options} placeholder="Axles" testid={`select-axles-${id}`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Gross vehicle weight rating (GVWR)</Label>
          <Sel value={detail.gvwr} onChange={(g) => onChange({ ...detail, gvwr: g })} options={meta.gvwr_ranges} placeholder="GVWR" testid={`select-gvwr-${id}`} />
        </div>
      </div>
    </div>
  );
}

export function StepEquipment({ profile, update, meta, t }) {
  const vehicles = profile.vehicles || [];
  const details = profile.vehicle_details || {};
  return (
    <div>
      <Field label={t("vehicles")} testid="field-vehicles">
        <CheckGrid testid="vehicle-option" options={meta.vehicles} selected={vehicles} onToggle={(v) => update({ vehicles: toggleIn(vehicles, v) })} />
      </Field>
      {vehicles.length > 0 && (
        <Field label="Axles & GVWR for each vehicle type" testid="field-vehicle-details">
          <div className="brutal-border">
            {vehicles.map((v) => (
              <VehicleDetail key={v} v={v} meta={meta} detail={details[v] || {}}
                onChange={(d) => update({ vehicle_details: { ...details, [v]: d } })} />
            ))}
          </div>
        </Field>
      )}
      <Field label="How many vehicles/trucks do you operate in total?" testid="field-fleet-size" hint="UCR fees are based on your fleet size.">
        <Input
          data-testid="input-fleet-size"
          type="number"
          inputMode="numeric"
          min={1}
          value={profile.fleet_size ?? ""}
          onChange={(e) => update({ fleet_size: e.target.value === "" ? null : Math.max(1, parseInt(e.target.value, 10) || 1) })}
          placeholder="e.g. 1"
          className="rounded-sm bg-[#141414] border-[#262626] h-11"
        />
      </Field>
      <Field label="Do you own your vehicle outright or lease it?" testid="field-ownership">
        <Sel value={profile.ownership} onChange={(v) => update({ ownership: v })} options={meta.ownership_options} placeholder="Select one" testid="select-ownership" />
      </Field>
      <Field label="What cargo will you haul? (select all that apply)" testid="field-cargo">
        <CheckGrid testid="cargo-option" options={meta.cargo_types} selected={profile.cargo_types || []}
          onToggle={(v) => update({ cargo_types: toggleIn(profile.cargo_types || [], v) })} />
      </Field>
      <Field label={t("eldCompany")} testid="field-eld">
        <Sel value={profile.eld_company} onChange={(v) => update({ eld_company: v })} options={meta.eld_companies} placeholder="Select ELD company" testid="select-eld-company" />
      </Field>
    </div>
  );
}

function DriverCard({ d, i, meta, onChange, onRemove, api, records, reload }) {
  return (
    <div className="brutal-border bg-[#0A0A0A] p-4 sm:p-5" data-testid={`driver-card-${i}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading font-bold">Driver {i + 1}</div>
        <ConfirmDelete testid={`remove-driver-${i}`} onConfirm={onRemove} confirmLabel="Delete driver"
          title={`Delete ${d.name || `Driver ${i + 1}`}?`}
          description="This removes the driver and all of their documents: license photos, CDL renewal and medical card. You'll have a few seconds to undo.">
          <button data-testid={`remove-driver-${i}`}
            className="min-h-[40px] px-3 border border-[#7F1D1D] text-[#F87171] hover:bg-[#DC2626] hover:border-[#DC2626] hover:text-white text-xs font-mono-label inline-flex items-center gap-1.5 transition-colors">
            <Trash2 size={14} /> Delete driver
          </button>
        </ConfirmDelete>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Driver name (optional)</Label>
          <Input data-testid={`input-driver-name-${i}`} value={d.name || ""} onChange={(e) => onChange({ ...d, name: e.target.value })}
            placeholder="e.g. John Smith" className="rounded-sm bg-[#141414] border-[#262626] h-11" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">CDL class</Label>
          <Sel value={d.cdl_class} onChange={(v) => onChange({ ...d, cdl_class: v })} options={meta.cdl_classes.map((c) => ({ value: c, label: `Class ${c}` }))} placeholder="Select CDL class" testid={`select-cdl-${i}`} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-muted-foreground mb-1.5 block">Driver type</Label>
          <Sel value={d.driver_type} onChange={(v) => onChange({ ...d, driver_type: v })} options={meta.driver_types} placeholder="Owner-Operator or Employee Driver" testid={`select-driver-type-${i}`} />
        </div>
      </div>
      <Label className="text-xs text-muted-foreground mb-1.5 block">Endorsements held (select all that apply)</Label>
      <CheckGrid testid={`endorsement-${i}`} options={meta.endorsements} selected={d.endorsements || []}
        onToggle={(v) => onChange({ ...d, endorsements: toggleIn(d.endorsements || [], v) })} />
      <EmergencyContact d={d} i={i} onChange={onChange} />
      <Label className="text-xs text-muted-foreground mt-4 mb-1.5 block">Driver's license photos</Label>
      <div className="brutal-border" data-testid={`driver-license-uploads-${i}`}>
        {["front", "back"].map((side) => {
          const id = `license_${side}:${d.id}`;
          return <ComplianceItem key={id} item={{ id, label: `Driver's license – ${side}`, dated: false, photo: true }} rec={records[id]} api={api} base="/compliance" onChanged={reload} />;
        })}
      </div>
    </div>
  );
}

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function StepDrivers({ profile, update, meta, api, persist }) {
  const drivers = profile.drivers || [];
  const set = (list) => update({ drivers: list });
  const [records, setRecords] = useState({});
  const reload = useCallback(() => api.get("/compliance").then(({ data }) => setRecords(Object.fromEntries(data.items.map((r) => [r.item_id, r])))).catch(() => {}), [api]);
  useEffect(() => { reload(); }, [reload]);
  const onPhoto = async () => { await persist(); reload(); };
  const removeDriver = async (d, i) => {
    try {
      const { data } = await api.delete(`/compliance/drivers/${encodeURIComponent(d.id)}`);
      update((p) => ({ drivers: (p.drivers || []).filter((x) => x.id !== d.id) }));
      reload();
      const undo = async () => {
        try {
          if (data.undo_token) await api.post(`/compliance/drivers/restore/${data.undo_token}`);
          update((p) => {
            const list = (p.drivers || []).filter((x) => x.id !== d.id);
            list.splice(Math.min(i, list.length), 0, d);
            return { drivers: list };
          });
          reload();
          toast.success("Driver restored");
        } catch {
          toast.error("Could not restore the driver.");
        }
      };
      toast.success("Driver deleted", { duration: 8000, action: { label: "Undo", onClick: undo } });
    } catch {
      toast.error("Could not delete the driver.");
    }
  };
  return (
    <div className="space-y-4">
      {drivers.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="drivers-empty">Add each driver who will operate your trucks, including yourself if you drive.</p>
      )}
      {drivers.map((d, i) => (
        <DriverCard key={d.id || i} d={d} i={i} meta={meta} api={api} records={records} reload={onPhoto}
          onChange={(nd) => set(drivers.map((x, j) => (j === i ? nd : x)))}
          onRemove={() => removeDriver(d, i)} />
      ))}
      <button
        onClick={() => set([...drivers, { id: newId(), name: "", cdl_class: "", endorsements: [], driver_type: "" }])}
        data-testid="add-driver-button"
        className="w-full min-h-[52px] border border-dashed border-[#FFD600]/60 hover:border-[#FFD600] hover:bg-[#141414] font-mono-label text-xs inline-flex items-center justify-center gap-2 transition-colors"
      >
        <Plus size={16} /> Add driver
      </button>
    </div>
  );
}
